// The ⚙ panel for a criteria prompt (General Process / Report) or a result-
// grid column — the same parts as the transaction field panel, minus what a
// prompt does not have. The host passes each control's value and what to do
// on a change; a hidden or read-only prompt always runs on its default.

import { useState } from 'react'
import { RotateCcw } from 'lucide-react'
import {
  AnchoredPanel, PanelHeader, PanelNote, PanelRow, PanelSection, PanelText, Switch,
} from './ui'

export interface PromptPanelProps {
  anchor: HTMLElement | null
  onClose: () => void
  title: string
  sub?: string
  saved?: boolean
  section?: string
  label: { value: string; standard?: string; customized?: boolean; onCommit: (v: string) => void; onReset?: () => void; caption?: string }
  show?: { on: boolean; onChange: (on: boolean) => void; title?: string; hint?: string }
  editable?: { on: boolean; onChange: (on: boolean) => void; disabled?: boolean; hint?: string }
  defaultValue?: { value: string; customized?: boolean; onCommit: (v: string) => void; onReset?: () => void; hint?: string; placeholder?: string }
  /** The prompt must have a default before it can be hidden / locked. */
  needsDefaultToFix?: boolean
  onResetAll?: () => void
  resetAllDisabled?: boolean
  note?: string
}

export default function PromptPanel(p: PromptPanelProps) {
  const [warn, setWarn] = useState<string | null>(null)
  const blockFix = (fixing: boolean) => {
    if (fixing && p.needsDefaultToFix && !String(p.defaultValue?.value ?? '').trim()) {
      setWarn(`Give "${p.title}" a default value first — a hidden or read-only prompt always runs on its default.`)
      return true
    }
    setWarn(null)
    return false
  }
  return (
    <AnchoredPanel anchor={p.anchor} onClose={p.onClose} label={p.title} width={400}>
      <PanelHeader title={p.title} sub={p.sub} saved={p.saved} />
      <div style={{ padding: '10px 16px 6px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {p.note && <PanelNote>{p.note}</PanelNote>}
        {p.section && <PanelSection title={p.section} />}
        <PanelText label={p.label.caption || 'Label'} value={p.label.value} standard={p.label.standard}
          customized={p.label.customized} onReset={p.label.onReset} onCommit={p.label.onCommit} />
        {p.show && (
          <PanelRow title={p.show.title || 'Show on the criteria form'} hint={p.show.hint}>
            <Switch label={p.show.title || 'Show'} on={p.show.on} onChange={(on) => {
              if (blockFix(!on)) return
              p.show!.onChange(on)
            }} />
          </PanelRow>
        )}
        {p.editable && (
          <PanelRow title="Editable" hint={p.editable.hint} dim={p.editable.disabled}>
            <Switch label="Editable" on={p.editable.on} disabled={p.editable.disabled} onChange={(on) => {
              if (blockFix(!on)) return
              p.editable!.onChange(on)
            }} />
          </PanelRow>
        )}
        {p.defaultValue && (
          <>
            <PanelText label="Default value" value={p.defaultValue.value} placeholder={p.defaultValue.placeholder}
              customized={p.defaultValue.customized} onReset={p.defaultValue.onReset}
              onCommit={(v) => { setWarn(null); p.defaultValue!.onCommit(v) }} />
            {p.defaultValue.hint && <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #6b7280)', marginTop: -4 }}>{p.defaultValue.hint}</span>}
          </>
        )}
        {warn && <PanelNote tone="warn">{warn} Not saved.</PanelNote>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px 14px', borderTop: '1px solid var(--color-border, #f1f3f6)', marginTop: 6 }}>
        {p.onResetAll
          ? <button type="button" onClick={p.onResetAll} disabled={p.resetAllDisabled}
              style={{ border: 0, background: 'transparent', color: '#6d28d9', cursor: 'pointer', fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 5, opacity: p.resetAllDisabled ? 0.45 : 1 }}>
              <RotateCcw size={13} />Reset
            </button>
          : <span />}
        <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #6b7280)' }}>Esc or click outside to close</span>
      </div>
    </AnchoredPanel>
  )
}
